/* PictoMini Dawn motion adapter v2.1.0.
 * Source: Dawn 15.5.0 assets/animations.js:6-49: one-time intersection reveal,
 * rootMargin '0px 0px -50px 0px', threshold 0; base.css:3305-3307: 75ms cascade.
 * Adaptations: visible first-screen content is skipped; no nested reveal;
 * group-local cascade, capped at seven delays (525ms); horizontal mobile rails
 * reveal together; a singleton observer; dynamic DOM/section lifecycle cleanup.
 * Runtime optimization: one startup scan; linear leaf/root selection; completed
 * media and protected hidden-state mutations do not rescan content.
 * No changes to uploads, variant selection, prices, cart refresh, or checkout.
 */
(() => {
  'use strict';
  if (window.PictoMiniDawnMotion) return;
  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pending = new Set();
  const timers = new Map();
  const queued = new Set();
  const seen = new WeakSet();
  const controller = new AbortController();
  let observer = null;
  let mutations = null;
  let queuedFlush = false;
  let destroyed = false;
  let failed = false;
  const counts = { armed: 0, revealed: 0, firstScreenSkipped: 0, failed: 0 };

  const protectedSelector = [
    'dialog', '[role="dialog"]', 'form', '.pm-header', '.pm-topbar', '.pm-announcement',
    '.pm-cart', '.pm-cart-drawer', '.pm-product__heading', '.pm-product__info',
    '.pm-mobile-cta', '.pv-mobile-cta', '.pm-quick-order', '.pv-builder',
    '[data-v2-builder]', '[data-v2-builder-sample]', '[data-combined-builder]',
    '[data-primary-upload]', '[data-file-field]', '[data-main-screen]',
    '[data-variant-price]', '[data-v2-price]', '[data-cart-count-badge]',
    '[name="checkout"]', '[data-product-submit]', '[data-v2-submit]',
    '.shopify-payment-button', '[data-pm-motion="off"]', '[data-pm-motion-reveal="off"]',
    '[data-pm-motion-skip]'
  ].join(',');
  const railSelector = '.pv-friends__rail,.pm-combined-videos__rail,.pm-designs__grid,.pv-home-shopping__grid,.pm-content-grid__cards';
  const groupSelector = `${railSelector},.pmc-grid,.pm-stories__grid,.pv-process__steps,.pv-promises__inner,.pp-review-grid,.pm-process-grid,.pm-trust__grid,.pm-included__grid`;
  const revealSelector = [
    '[data-pm-reveal]', '[data-pm-dawn-reveal]',
    '.pv-personal__copy', '.pv-personal__example', '.pv-editorial__image', '.pv-editorial__copy',
    '.pv-section-head', '.pv-friends__heading', '.pv-home-shopping__heading', '.pm-designs__heading',
    '.pv-process__steps > *', '.pv-promises__inner > *', '.pv-home-shopping__grid > li',
    '.pv-friends__rail > *', '.pm-designs__item', '.pv-closing', '.pv-pricing > div',
    '.pv-home-faq > .pv-heading', '.pv-faq details', '.pm-faq__items details',
    '.pm-content-grid__heading', '.pm-content-grid__intro', '.pm-content-grid__card', '.pm-tabs__heading', '.pm-tabs__intro',
    '.pm-combined-videos__inner > h2', '.pm-combined-videos__card',
    '.pm-stories__header', '.pm-stories__card', '.pm-process-grid > *', '.pm-trust__grid > *', '.pm-kit-card',
    '.pmt__media', '.pmt__content', '.pml-story__header', '.pml-story__row', '.pml-story__faq',
    '.pmba-section__content', '.pmba-section__media', '.pmc-comparison__heading', '.pmc-comparison__intro', '.pmc-comparison__scroll',
    '.pmc-heading', '.pmc-card', '.pp-details-heading', '.pp-description', '.pp-detail-model',
    '.pp-faq > header', '.pp-faq details', '.pp-reviews__header', '.pp-review-card', '.pp-review-empty',
    '.pm-page__article > h1', '.pm-page__article > .pm-rte', '.pm-footer__grid > *', railSelector
  ].join(',');
  const disclosureSelector = 'details.pm-product-nav,details.pm-mobile-nav,details.pm-config-menu,details.pm-localization__disclosure,details.pmc-filter,details.pm-photo-tips,.pv-faq details,.pm-faq details,.pp-faq details,details.pml-story__faq';
  const active = () => !failed && !destroyed && html.dataset.pmMotion === 'on' && html.dataset.pmMotionReveal === 'on' && !reduce.matches;

  function query(root, selector) {
    const nodes = Array.from(root.querySelectorAll(selector));
    if (root instanceof Element && root.matches(selector)) nodes.unshift(root);
    return nodes;
  }
  function clearNode(node) {
    observer?.unobserve(node);
    pending.delete(node);
    if (timers.has(node)) window.clearTimeout(timers.get(node));
    timers.delete(node);
    node.classList.remove('pm-dawn-waiting', 'pm-dawn-revealing', 'pm-dawn-panel-enter');
    node.style.removeProperty('--pm-dawn-order');
  }
  function stop() {
    observer?.disconnect();
    [...pending, ...timers.keys()].forEach(clearNode);
    observer = null;
  }
  function failOpen() {
    failed = true;
    counts.failed += 1;
    stop();
    html.dataset.pmMotionRuntime = 'failed';
  }
  function guarded(fn) {
    try { return fn(); } catch (error) { failOpen(); console.warn('PictoMini motion restored visible content.', error); }
  }
  function horizontalRail(node) {
    if (!node.matches(railSelector) || node.scrollWidth <= node.clientWidth + 1) return false;
    return /auto|scroll/.test(window.getComputedStyle(node).overflowX);
  }
  function visibleOrPassed(node, rect) {
    if (rect.bottom <= 0) return true;
    return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
  }
  function arm(root) {
    if (!active() || !observer || (root instanceof Element && root.closest(protectedSelector))) return;
    const candidates = query(root, revealSelector).filter((node) => {
      // Completed/registered nodes are structural leaves only; they cannot be
      // armed again, so repeated refresh need not repeat DOM eligibility queries.
      if (seen.has(node) || pending.has(node)) return true;
      if (node.closest(protectedSelector) || node.querySelector(protectedSelector)) return false;
      if (node.matches(railSelector)) return horizontalRail(node);
      const rail = node.closest(railSelector);
      return !rail || !horizontalRail(rail);
    });
    // Mark candidate ancestors once, keeping innermost candidates without the
    // pairwise contains scan. Include seen/pending nodes in this structural pass
    // so repeated refresh cannot arm a previously skipped wrapper.
    const candidateSet = new Set(candidates);
    const nonLeaves = new Set();
    const walkedAncestors = new WeakSet();
    candidates.forEach((node) => {
      for (let ancestor = node.parentElement; ancestor && !walkedAncestors.has(ancestor); ancestor = ancestor.parentElement) {
        walkedAncestors.add(ancestor);
        if (candidateSet.has(ancestor)) nonLeaves.add(ancestor);
      }
    });
    candidates.forEach((node) => {
      if (nonLeaves.has(node)) return;
      if (seen.has(node) || pending.has(node) || node.querySelector('.pm-dawn-waiting,.pm-dawn-revealing')) return;
      if (node.parentElement?.closest('.pm-dawn-waiting,.pm-dawn-revealing')) return;
      if (!node.getClientRects().length) return; // Hidden tabs/late media are reconsidered when shown.
      const rect = node.getBoundingClientRect();
      if (visibleOrPassed(node, rect)) { seen.add(node); counts.firstScreenSkipped += 1; return; }
      if (rect.height === 0 || rect.width === 0) return;
      observer.observe(node); // Register successfully before introducing an off-screen state.
      pending.add(node);
      node.classList.add('pm-dawn-waiting');
      counts.armed += 1;
    });
  }
  function intersect(entries) {
    guarded(() => {
      if (!active()) { stop(); return; }
      const orders = new Map();
      const visible = entries.filter((entry) => entry.isIntersecting && pending.has(entry.target));
      visible.sort((a, b) => a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_PRECEDING ? 1 : -1);
      visible.forEach(({ target: node }) => {
        if (!node.isConnected) { clearNode(node); return; }
        const group = node.closest(groupSelector);
        const order = group ? Math.min(orders.get(group) || 0, 7) : 0;
        if (group) orders.set(group, order + 1);
        observer.unobserve(node);
        pending.delete(node);
        seen.add(node);
        node.style.setProperty('--pm-dawn-order', String(order));
        node.classList.remove('pm-dawn-waiting');
        node.classList.add('pm-dawn-revealing');
        timers.set(node, window.setTimeout(() => guarded(() => clearNode(node)), 600 + order * 75 + 50));
        counts.revealed += 1;
      });
    });
  }
  function prepareDisclosures(root) {
    query(root, disclosureSelector).forEach((node) => {
      if (!node.open) node.classList.add('pm-dawn-disclosure-ready');
    });
  }
  function refresh(root = document) {
    guarded(() => { if (!destroyed) { prepareDisclosures(root); arm(root); } });
  }
  function sync() {
    guarded(() => {
      if (!active() || !('IntersectionObserver' in window)) { stop(); return; }
      if (!observer) observer = new IntersectionObserver(intersect, { rootMargin: '0px 0px -50px 0px', threshold: 0 });
      refresh();
      html.dataset.pmMotionRuntime = 'ready';
    });
  }
  function cleanRoot(root) {
    [...pending, ...timers.keys()].forEach((node) => { if (node === root || root.contains(node)) clearNode(node); });
    queued.delete(root);
  }
  function queue(root) {
    if (destroyed || !(root instanceof Element) || root.closest(protectedSelector)) return;
    queued.add(root);
    if (queuedFlush) return;
    queuedFlush = true;
    queueMicrotask(() => guarded(() => {
      queuedFlush = false;
      const roots = [...queued];
      queued.clear();
      const rootSet = new Set(roots);
      roots.filter((node) => {
        if (!node.isConnected) return false;
        for (let ancestor = node.parentElement; ancestor; ancestor = ancestor.parentElement) {
          if (rootSet.has(ancestor)) return false;
        }
        return true;
      }).forEach(refresh);
    }));
  }
  function panelEnter(node) {
    if (reduce.matches || html.dataset.pmMotion !== 'on' || node.closest('[data-pm-motion="off"]') || node.hidden) return;
    if (node.closest('.pm-dawn-waiting,.pm-dawn-revealing')) return;
    if (timers.has(node)) window.clearTimeout(timers.get(node));
    node.classList.add('pm-dawn-panel-enter');
    timers.set(node, window.setTimeout(() => guarded(() => clearNode(node)), 250));
  }
  function start() {
    guarded(() => {
      sync();
      if (!observer) prepareDisclosures(document);
      mutations = new MutationObserver((records) => guarded(() => {
        let changedSettings = false;
        records.forEach((record) => {
          if (record.type === 'attributes') {
            if (record.attributeName === 'hidden') {
              if (record.target.closest(protectedSelector)) return;
              if (!record.target.hidden) { queue(record.target); if (record.target.matches('.pm-tabs__panel')) panelEnter(record.target); }
              else cleanRoot(record.target);
            } else if (record.attributeName !== 'data-pm-motion-hover') { changedSettings = true; }
          } else {
            record.removedNodes.forEach((node) => { if (node instanceof Element && !node.isConnected) cleanRoot(node); });
            record.addedNodes.forEach(queue);
          }
        });
        if (changedSettings) { stop(); sync(); }
      }));
      mutations.observe(html, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'data-pm-motion', 'data-pm-motion-reveal', 'data-pm-motion-hover'] });
      document.addEventListener('toggle', (event) => guarded(() => {
        if (event.target instanceof Element && event.target.matches(disclosureSelector) && !event.target.open) event.target.classList.add('pm-dawn-disclosure-ready');
      }), { capture: true, signal: controller.signal });
      document.addEventListener('load', (event) => guarded(() => {
        if (event.target instanceof Element && event.target.matches('img,video,iframe')) {
          const candidate = event.target.closest(revealSelector);
          if (candidate && !seen.has(candidate) && !pending.has(candidate)) queue(candidate);
        }
      }), { capture: true, signal: controller.signal });
      document.addEventListener('focusin', (event) => guarded(() => {
        const candidate = event.target instanceof Element && event.target.closest('.pm-dawn-waiting,.pm-dawn-revealing');
        if (candidate) { seen.add(candidate); clearNode(candidate); }
      }), { signal: controller.signal });
      document.addEventListener('shopify:section:load', (event) => refresh(event.target), { signal: controller.signal });
      document.addEventListener('shopify:section:unload', (event) => guarded(() => cleanRoot(event.target)), { signal: controller.signal });
      document.addEventListener('shopify:section:reorder', () => guarded(() => { stop(); sync(); }), { signal: controller.signal });
      // Selecting an editor block must make it immediately available to edit.
      document.addEventListener('shopify:block:select', (event) => guarded(() => cleanRoot(event.target)), { signal: controller.signal });
      window.addEventListener('pagehide', () => guarded(stop), { signal: controller.signal });
      window.addEventListener('pageshow', (event) => { if (event.persisted) sync(); }, { signal: controller.signal });
      window.addEventListener('error', (event) => { if ((event.filename || '').includes('pictomini-dawn-motion.js')) failOpen(); }, { signal: controller.signal });
      if (reduce.addEventListener) reduce.addEventListener('change', sync, { signal: controller.signal });
      else reduce.addListener(sync);
    });
  }
  function destroy() {
    if (destroyed) return;
    stop();
    mutations?.disconnect();
    controller.abort();
    if (!reduce.addEventListener) reduce.removeListener(sync);
    queued.clear();
    destroyed = true;
  }
  window.PictoMiniDawnMotion = {
    version: '2.1.0', refresh, destroy,
    stats: () => ({ ...counts, pending: pending.size, timers: timers.size, observers: observer ? 1 : 0, reducedMotion: reduce.matches, active: active() })
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: controller.signal });
  else start();
})();
